/**
 * Knowledge Base Service - Handles API interactions for knowledge base management
 */

import axios from 'axios';
import { post, put } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/bluemormon/knowledgebase`;
const CHAT_LIST_API = `/api/v1/persistence/conversation`;
const MESSAGE_LIST_API = `/api/v1/persistence/message`;
const DOC_META_API = `/api/v1/persistence/document_metadata`;


/**
 * Ingest single File 
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {FormData} formData - Form data containing the file
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const ingestFileToKb = async (knowledgeBaseId, fileNameList, otherOptions, pii_flag) => {
    let payload = {
        document_urls: fileNameList,
        payload: otherOptions,
        "async_execution": true,
        "mask_pii_data": pii_flag
    }
    try {
        const response = await post(`${API_URL}/${knowledgeBaseId}/ingest`, payload);
        return response;
    } catch (error) {
        console.error(`Error uploading file to knowledge base ${knowledgeBaseId}:`, error);
        throw error;
    }
};

/**
 * Get all Chat Transactions with optional filtering
 * @param {Object} filters - Filter parameters
 * @param {Object} sort - Sort parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const getAllChatsByFilter = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${CHAT_LIST_API}/fetch?query=${query}`, { filter: filter, sort });
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Get all Chat Messages with optional filtering
 * @param {Object} filters - Filter parameters
 * @param {Object} sort - Sort parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const getAllMessagesByFilter = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${MESSAGE_LIST_API}/fetch?query=${query}`, { filter: filter, sort });
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Create ChatList
 * @param {Object} chatpayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const createChatForKb = async (chatpayload) => {
    try {
        const response = await post(`${CHAT_LIST_API}`, chatpayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};
/**
 * Create MessageList
 * @param {Object} messagepayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const createMessageForChat = async (messagepayload) => {
    try {
        const response = await post(`${MESSAGE_LIST_API}`, messagepayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};

/**
 * Update ChatList
 * @param {Object} chatpayload - Chat parameters
 * @returns {Promise<Array>} Promise resolving to array of knowledge bases
 */
export const updateChatForKb = async (chatpayload) => {
    try {
        const response = await put(`${CHAT_LIST_API}`, chatpayload);
        return response;
    } catch (error) {
        console.error('Error fetching knowledge bases:', error);
        throw error;
    }
};


/**
 * Search within a knowledge base
 * @param {string} knowledgeBaseId - Knowledge base ID
 * @param {string} query - Search query
 * @param {Object} options - Search options
 * @returns {Promise<Object>} Promise resolving to search results
 */
export const searchKnowledgeBase = async (knowledgeBaseId, query, context, conversation_id, type) => {
    try {
        const response = await post(`${API_URL}/${type}`, {
            query: query,
            context: context,
            knowledge_base: knowledgeBaseId,
            conversation_id: conversation_id
        });
        return response.response;
    } catch (error) {
        console.error(`Error searching knowledge base ${knowledgeBaseId}:`, error);
        throw error;
    }
};

/**
 * Search Doc meta data
 * @param {string} query - Search query
 * @param {Object} filter - Search options
 * @param {Object} sort - Search options
 * @returns {Promise<Object>} Promise resolving to search results
 */
export const getDocMetaDataWithFilters = async (filter = {}, sort = {}, query = null) => {
    try {
        const response = await post(`${DOC_META_API}/fetch?query=${query}`, {
            filter: filter,
            sort: sort
        });
        return response;
    } catch (error) {
        console.error(`Error searching knowledge base:`, error);
        throw error;
    }
};

export default {
    ingestFileToKb,
    getAllChatsByFilter,
    searchKnowledgeBase,
    getAllMessagesByFilter,
    getDocMetaDataWithFilters
};

